import type {
	BlogCategory,
	Category,
	DiyProject,
	FaqCategory,
	FaqItem,
	MiniSearchQuery,
	Page,
	Product,
	Sheet,
	Stylist,
} from "@pimcore";
import { LoadIcon, Typography, Wrap, cn, getThumbnail } from "@tosize/ui";
import { AspectRatio } from "radix-ui";
import { type ComponentProps, useMemo } from "react";
import { Link } from "~/components/Link";
import { formatTitle } from "~/components/layout/Header/Search/utils";
import Image from "~/components/utilities/Image";

interface SearchResultProps extends ComponentProps<"div"> {
	variant?: "mini" | "default";
	lang: string;
	customerSearch?: boolean;
	loading: boolean;
	data?: MiniSearchQuery;
	innerClassName?: string;
	searchText: string;
	messages: {
		loading: string;
		noResults: string;
	};
}

export default function SearchResult(props: SearchResultProps) {
	const {
		variant = "default",
		customerSearch,
		loading,
		data,
		innerClassName,
		searchText,
		messages,
		...remainingProps
	} = props;

	return (
		<div {...remainingProps}>
			{loading && (
				<Typography
					variant="paragraph"
					color="text-shades-dark"
					className={cn(innerClassName, "truncate pb-2")}
				>
					<LoadIcon />
					<span className="ml-2">{messages.loading}</span>
				</Typography>
			)}
			{customerSearch
				? data?.miniSearch
						.filter(
							(category) =>
								(!!category.items.length && category.code === "page_customer_service") ||
								category.code === "page_tips",
						)
						.map((category) => (
							<div key={category.code} className="pt-1 pb-2 first:pt-0">
								<Typography
									variant="paragraph-italic"
									color="text-shades-dark"
									size="text-sm"
									className={cn(innerClassName, "truncate px-1 py-1 text-left")}
								>
									<span dangerouslySetInnerHTML={{ __html: category.name }} />
								</Typography>
								{category.items.map((item, index) => (
									<ResultItem
										lang={props.lang}
										type={category.code === "diy_project" ? "card" : "row"}
										item={item}
										code={category.code}
										searchText={searchText}
										key={index}
									/>
								))}
							</div>
						))
				: data?.miniSearch
						.filter((category) => !!category.items.length)
						.map((category) => (
							<div key={category.code} className="pt-1 pb-2 first:pt-0">
								<Typography
									variant="paragraph-italic"
									color="text-shades-dark"
									size="text-sm"
									className={cn(innerClassName, "truncate px-1 py-1")}
								>
									<span dangerouslySetInnerHTML={{ __html: category.name }} />
								</Typography>

								<Wrap
									if={category.code === "diy_project"}
									with={(children) => (
										<div
											className={cn("m-1 grid gap-2", {
												"grid-cols-2 2xl:grid-cols-3": variant === "mini",
												"grid-cols-1 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5":
													variant === "default",
											})}
										>
											{children}
										</div>
									)}
								>
									{category.items.map((item, index) => (
										<ResultItem
											lang={props.lang}
											type={category.code === "diy_project" ? "card" : "row"}
											item={item}
											code={category.code}
											searchText={searchText}
											key={index}
										/>
									))}
								</Wrap>
							</div>
						))}

			{(!data || data?.miniSearch.every((category) => !category.items.length)) && !loading && (
				<Typography
					variant="paragraph"
					color="text-shades-dark"
					className={cn(innerClassName, "truncate pb-2")}
				>
					{messages.noResults}
				</Typography>
			)}
		</div>
	);
}

interface ResultsProps {
	lang: string;
	type?: "row" | "card";
	item: Partial<
		Category | DiyProject | FaqCategory | FaqItem | Page | Product | Sheet | Stylist | BlogCategory
	>;
	code: string;
	searchText: string;
	className?: string;
}

function ResultItem({ lang, type = "row", item, code, searchText }: ResultsProps) {
	const image = useMemo(() => {
		switch (item.__typename) {
			case "Product":
			case "Page":
				return code !== "page_customer_service" ? item.image : undefined;
			case "DIYProject":
				return item.images?.length ? item.images[0] : undefined;
		}
	}, [code, item]);

	const url = useMemo(() => {
		switch (item.__typename) {
			case "Category":
			case "Product":
			case "Stylist":
			case "DIYProject":
			case "Page":
			case "FaqItem":
				return item.url;
		}
	}, [item]);

	const title = useMemo(() => {
		switch (item.__typename) {
			case "Category":
			case "Product":
			case "Stylist":
				return item.name;
			case "DIYProject":
			case "FaqCategory":
			case "FaqItem":
			case "Page":
				return item.title;
		}
	}, [item]);

	if (type === "card") {
		return (
			<ResultItemCard
				title={title}
				destructedTitle={title ? formatTitle(title, searchText) : undefined}
				url={url}
				scroll={item.__typename !== "FaqItem"}
				image={image ? getThumbnail(image, lang, "watermark_800x450") : undefined}
			/>
		);
	}

	return (
		<ResultItemRow
			title={title}
			destructedTitle={title ? formatTitle(title, searchText) : undefined}
			url={url}
			scroll={item.__typename !== "FaqItem"}
			image={
				image
					? getThumbnail(
							image,
							lang,
							code === "page_tips" ? "watermark_800x450" : "watermark_310x155",
						)
					: undefined
			}
		/>
	);
}

interface ResultItemProps {
	url?: string;
	scroll?: boolean;
	title?: string;
	destructedTitle?: string;
	image?: string;
	className?: string;
}

function ResultItemRow({ url, scroll, title, destructedTitle, image, className }: ResultItemProps) {
	if (!(url && title)) {
		return null;
	}

	return (
		<Link
			href={url}
			scroll={scroll}
			passHref
			className={cn(
				className,
				"flex w-full items-center space-x-4 rounded py-1 text-left hover:bg-shades-light focus:outline-none focus-visible:bg-shades-light",
				{ "px-1": image, "px-2": !image },
			)}
			title={title}
		>
			{!!image && (
				<div className="flex min-h-[calc(155px/4)] min-w-[calc(310px/4)] shrink-0 overflow-hidden rounded bg-shades-medium-light">
					<Image
						src={image}
						width={310 / 4}
						height={155 / 4}
						alt={title}
						className="bg-cover bg-left-bottom"
					/>
				</div>
			)}
			<Typography variant="paragraph" className="truncate">
				<span dangerouslySetInnerHTML={{ __html: destructedTitle ?? title }} />
			</Typography>
		</Link>
	);
}

function ResultItemCard({ url, title, destructedTitle, image, className }: ResultItemProps) {
	if (!(url && title)) {
		return null;
	}

	return (
		<Link
			href={url}
			passHref
			title={title}
			className={cn(
				className,
				"flex flex-col overflow-auto rounded-xl bg-shades-light outline-none transition-all hover:shadow-md focus-visible:shadow-md",
			)}
		>
			{!!image && (
				<AspectRatio.Root ratio={16 / 9}>
					<Image src={image} alt={title} fill sizes="100vw" className="bg-cover bg-left-bottom" />
				</AspectRatio.Root>
			)}
			<div className="flex h-full flex-col p-3">
				<span
					className="break-words font-sans text-dark leading-tight"
					dangerouslySetInnerHTML={{ __html: destructedTitle ?? title }}
				/>
			</div>
		</Link>
	);
}
